import _ from 'lodash';
import { setCookie } from 'utils/cookies';
import * as Sentry from '@sentry/react';
import _debounce from 'lodash/debounce';
import { DEBOUNCE_TIME } from 'constants/config';
import { BASE_URL } from './urls';

const Utils = {
  objectIsEmpty: (obj) => {
    if (!obj) {
      return false;
    }
    for (var prop in obj) {
      if (obj.hasOwnProperty(prop)) {
        return false;
      }
    }

    return JSON.stringify(obj) === JSON.stringify({});
  },

  getObjectWithKeyInArray: (array, key, value) => {
    if (!array || array.length === 0) {
      return null;
    } else {
      const object = array.find((obj) => obj[key] === value);
      return !!object ? object : null;
    }
  },

  monthsList: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  monthListInNumbers: ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'],
  colorCode: {
    DONE: 'done',
    FAIL: 'fail',
    PENDING: 'pending',
    RECEIVED: 'done',
    APPROVED: 'pending',
    INITIATED: 'pending',
  },

  copyToClipboard: (address) => {
    const pocRows = address.pocs.map((poc) => {
      return { name: poc.name, number: poc.number };
    });

    const entries = {
      'Address Details': address.name,
      'POC List': pocRows,
    };
    navigator.clipboard.writeText(JSON.stringify(entries));
  },

  reAuthenticate: async () => {
    try {
      const res = await fetch(`${BASE_URL}/user/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          accept: 'application/json, text/plain, */*',
        },
      });

      if (!res.ok) {
        const bodyText = await res.text().catch(() => '');
        throw new Error(`Logout failed: ${res.status} ${res.statusText}${bodyText ? ` - ${bodyText}` : ''}`);
      }
      setCookie('googleAuth', ';expires=Thu, 01 Jan 1970 00:00:01 GMT;', 8);
      setCookie('token', ';expires=Thu, 01 Jan 1970 00:00:01 GMT;', 8);
      setCookie('email', ';expires=Thu, 01 Jan 1970 00:00:01 GMT;', 8);
      window.localStorage.removeItem('userInfo');
      window.location.href = window.location.origin + '/portal/login';
    } catch (error) {
      Sentry.captureException(error);
    }
  },

  dateToTimeString: (date) => {
    let h = date.getHours();
    let m = date.getMinutes();
    const t = h >= 12 ? 'pm' : 'am';
    h = h % 12;
    h = h ? h : 12; // the hour '0' should be '12'
    m = m < 10 ? '0' + m : m;
    return h + ':' + m + ' ' + t;
  },

  dateToTimeIn24: (date) => {
    let h = date.getHours();
    let m = date.getMinutes();
    return `${h < 10 ? `0${h}` : h}:${m < 10 ? `0${m}` : m}`;
  },

  getNameFromEmail: (email) => {
    if (!email || typeof email !== 'string') return '';
    const regex = /^[^@]+/;
    const match = email.match(regex);
    return match ? match[0] : email;
  },

  getDate: (stamp) => {
    let dateValue = new Date(stamp * 1000);
    return dateValue.toString();
  },

  getTimeStamp: (date) => {
    return Number((new Date(date).getTime() / 1000).toFixed(0));
  },

  epochToDateTimeString: (epoch) => {
    try {
      const utcSeconds = Math.floor(parseInt(epoch));
      let date = new Date(0);
      date.setUTCSeconds(utcSeconds);
      return (
        date.getDate() +
        ' ' +
        Utils.monthsList[date.getMonth()] +
        ' ' +
        date.getFullYear() +
        ', ' +
        Utils.dateToTimeString(date)
      );
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
  },

  epochToDateTimeStringFormat2: (epoch) => {
    try {
      const utcSeconds = Math.floor(parseInt(epoch));
      let date = new Date(0);
      date.setUTCSeconds(utcSeconds);
      return `
        ${date.getDate()} ${Utils.monthsList[date.getMonth()]} ${date
        .getFullYear()
        .toString()
        .substring(2)}, ${Utils.dateToTimeIn24(date)}
      `;
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
  },

  epochToMinuteString: (epoch) => {
    try {
      const utcSeconds = Math.floor(parseInt(epoch));
      let date = new Date(0);
      date.setUTCSeconds(utcSeconds);
      return `${date.getMinutes()} min`;
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
  },

  getCleanedUpperCaseString: (rawString) => {
    try {
      if (!!rawString) {
        const result = (rawString + '').toUpperCase().trim();
        return result;
      }
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
    return '';
  },

  getCleanedAlphanumericString: (rawString, forceUpperCase = false) => {
    try {
      if (!!rawString) {
        let result = rawString.replace(/[^0-9a-zA-Z]+/gi, '');
        if (forceUpperCase) {
          result = result.toUpperCase();
        }
        return result;
      }
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
    return '';
  },

  getStringFromDivision: (dividend, divisor) => {
    try {
      if (!!dividend && !!divisor && divisor !== 0) {
        const result = (dividend / divisor).toFixed(2);
        return result + '';
      }
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
    return '';
  },

  getDecimalPlacesParsedNumber: (number, decimalPlaces) => {
    try {
      return Number.parseFloat(number).toFixed(decimalPlaces);
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
    }
    return '';
  },

  objectKeysToSnakeCase: (camelCaseObject) => {
    var snake_case_object = {};
    _.forEach(camelCaseObject, function (value, key) {
      if (_.isPlainObject(value) || _.isArray(value)) {
        // checks that a value is a plain object or an array - for recursive key conversion
        value = Utils.objectKeysToSnakeCase(value); // recursively update keys of any values that are also objects
      }
      snake_case_object[_.snakeCase(key)] = value;
    });
    return snake_case_object;
  },

  convertToTitleCase: (str) => {
    try {
      var splitStr = String(str).toLowerCase().split(' ');
      for (var i = 0; i < splitStr.length; i++) {
        splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
      }
      return splitStr.join(' ');
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
  },

  convertToDateString: (epoch) => {
    const date = new Date(epoch * 1000);
    const dateTimeFormat = new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short', day: '2-digit' });
    const [{ value: month }, , { value: day }, , { value: year }] = dateTimeFormat.formatToParts(date);
    return `${day}-${month}-${year}`;
  },

  returnPrefillingDate: (epoch) => {
    const date = Utils.convertToDateString(epoch);
    const year = date.split('-')[2];
    const day = date.split('-')[0];
    let monthNumber = Utils.monthsList.indexOf(date.split('-')[1]) + 1;
    monthNumber = monthNumber < 10 ? '0' + monthNumber : monthNumber;
    return `${year}-${monthNumber}-${day}`;
  },

  defaultDateParser: (epoch) => {
    const timestamp = epoch * 1000;
    let dateValue = new Date(timestamp).toDateString();
    let dateValueList = dateValue.split(' ');
    let monthNumber = Utils.monthsList.indexOf(dateValueList[1]) + 1;
    monthNumber = monthNumber < 10 ? '0' + monthNumber : monthNumber;
    let convertedDate = dateValueList[3] + '-' + monthNumber + '-' + dateValueList[2];
    let timeValue = new Date(timestamp).toLocaleTimeString('en-GB');
    return convertedDate + 'T' + timeValue.substr(0, 5);
  },

  defaultDateOnlyParser: (epoch) => {
    var timestamp = epoch * 1000;
    var dateValue = new Date(timestamp).toISOString();
    return dateValue.split('T')[0];
  },

  secondstoDhm: (epoch) => {
    epoch = Number(epoch);
    var d = Math.floor(epoch / (3600 * 24));
    var h = Math.floor((epoch % (3600 * 24)) / 3600);
    var m = Math.floor((epoch % 3600) / 60);

    var dDisplay = d > 0 ? d + (d === 1 ? 'd ' : 'd ') : '';
    var hDisplay = h > 0 ? h + (h === 1 ? 'h ' : 'h ') : '';
    var mDisplay = m > 0 ? m + (m === 1 ? 'min' : 'mins') : '';
    return dDisplay + hDisplay + mDisplay;
  },

  splitUnderscore: (str) => {
    var strList = str.split('_');
    var out = '';
    for (var i = 0; i < strList.length; i++) {
      out += strList[i][0].toUpperCase() + strList[i].slice(1).toLowerCase() + ' ';
    }
    return out;
  },
  isValidTAN: (tan) => {
    const tanPattern = /^[A-Za-z0-9]{10}$/;
    return tanPattern.test(tan);
  },
  getCategory: (number, cat) => {
    const categories = [
      { range: [0, 1], category: 'Less than 1' },
      { range: [1, 3], category: '1-3' },
      { range: [3, 5], category: '3-5' },
      { range: [5, 8], category: '5-8' },
      { range: [8, 10], category: '8-10' },
      { range: [10, 15], category: '10-15' },
      { range: [15, 20], category: '15-20' },
      { range: [20, 30], category: '20-30' },
      { range: [30, 50], category: '30-50' },
      { range: [50, Infinity], category: 'More than 50' },
    ];

    if (cat) return categories.map((x) => x.category);
    const matchedCategory = categories.find(({ range }) => number >= range[0] && number < range[1]);

    return matchedCategory ? matchedCategory.category : 'Unavailable';
  },
  truncate: (string, length) => {
    if (!string) {
      return '';
    }
    if (string && string.length > length) {
      return string.substring(0, length - 2) + '..';
    } else {
      return string;
    }
  },
  detectMob: () => {
    return window && navigator.userAgent.toLowerCase().indexOf('mobile') !== -1;
  },
  logout: () => {
    document.cookie = '';
    window.localStorage.clear();
    Utils.reAuthenticate();
  },
  getUserDataFromLocal: () => {
    let stringifiedData = window.localStorage.getItem('userInfo');
    return stringifiedData && JSON.parse(stringifiedData);
  },
  epochToInputDateTimeString: (epoch) => {
    try {
      let date = new Date(epoch * 1000);
      return date.getFullYear() + '-' + Utils.monthListInNumbers[date.getMonth()] + '-' + date.getDate();
    } catch (error) {
      Sentry.captureException(error);

      console.log(error);
      return '';
    }
  },
  parseAmount: (amount) => {
    return parseAmount({ amount });
  },
  slimifyArrayOfObjects: (array, keys) => {
    return array.map((object) => {
      const slimObject = {};
      keys.forEach((key) => {
        slimObject[key] = object[key];
      });
      return slimObject;
    });
  },
  objectToArray: (object) => {
    return Object.keys(object).map((key) => object[key]);
  },
  isPTLPage: (pathname) => {
    return pathname === '/portal/ptl-ticketing';
  },
};

const currencyMapper = {
  INR: '₹',
};

export const parseAmount = ({ amount, currency = 'INR' }) => (amount ? `${currencyMapper[currency]} ${amount}` : '');

const fallbackCopyTextToClipboard = (text) => {
  return new Promise((resolve, reject) => {
    try {
      var textArea = document.createElement('textarea');
      textArea.value = text;

      // Avoid scrolling to bottom
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      var successful = document.execCommand('copy');
      var msg = successful ? 'successful' : 'unsuccessful';
      resolve();
      document.body.removeChild(textArea);
    } catch (err) {
      Sentry.captureException(err);

      reject('Fallback: Oops, unable to copy', err);
    }
  });
};

export const copyTextToClipboard = (text) => {
  if (!navigator.clipboard) {
    return fallbackCopyTextToClipboard(text);
  }
  return navigator.clipboard.writeText(text);
};

export const getEnv = () => {
  if (window.location.hostname === 'localhost') {
    return 'dev';
  } else if (
    window.location.hostname === 'odin.wheelseye.com' ||
    window.location.hostname === 'odin.stage.wheelseye.in' ||
    window.location.hostname === 'odin.dev.wheelseye.in' ||
    (window.location.hostname.startsWith('odin-') && window.location.hostname.endsWith('.stage.wheelseye.in'))
  ) {
    return 'prod';
  } else {
    return false;
  }
};

export const parseOptions = (options, keyName, valName) => {
  return options.map((option) => {
    return {
      label: option[valName],
      value: option[keyName],
    };
  });
};

export const getDefaultOptions = (options) => {
  if (_.isEmpty(options)) return [];
  return options.map((option) => {
    return {
      label: option,
      value: option,
    };
  });
};

export const loadScript = async (fileUrl, async = true, type = 'text/javascript') => {
  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.type = type;
      script.async = async;
      script.src = fileUrl;

      script.addEventListener('load', (ev) => {
        resolve({ status: true });
      });

      script.addEventListener('error', (ev) => {
        reject({
          status: false,
          message: `Failed to load the script ＄{fileUrl}`,
        });
      });

      document.body.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });
};

export const createDebouncedFunction = (func, delay = DEBOUNCE_TIME) => {
  return _debounce(func, delay);
};

export const podDetectionStatuses = [
  { key: 'NON_IDEAL_BLUR', value: 'Non Ideal - Blur' },
  { key: 'NON_IDEAL_CROPPED', value: 'Non Ideal - Cropped' },
  { key: 'NON_IDEAL_MULTIPLE_DOCUMENT', value: 'Non Ideal - Multiple Document' },
  { key: 'NON_IDEAL_STAMP_SIGN_MISSING', value: 'Non Ideal - Stamp & sign missing' },
  { key: 'NON_IDEAL_CX_NOT_AGREE', value: 'Non Ideal - CX not agree' },
  { key: 'IDEAL', value: 'Ideal' },
];

export const getPodDetectionStringFromEnum = (key) => {
  return podDetectionStatuses.find((item) => item?.key === key)?.value;
};

export default Utils;
